/**
 * Hook for managing script edit state in the ScriptPreviewModal.
 *
 * Handles mutable state for per-section editing, voice/TTS overrides,
 * and resolution changes. Pure client-side state, separate from the
 * server-side preview lifecycle in useScriptPreview.
 */

import React, { useState, useCallback, useMemo } from 'react';

import type { ScriptSection } from '../types';

export interface EditableSection {
  title: string;
  narration_text: string;
  duration_seconds: number;
  isExpanded: boolean;
}

export interface VoiceOverrides {
  tts_provider?: string;
  voice_id?: string;
}

export interface ScriptEditOverrides {
  tts_provider?: string;
  voice_id?: string;
  resolution?: string;
  sections?: ScriptSection[];
}

interface UseScriptEditStateReturn {
  sections: EditableSection[];
  voiceOverrides: VoiceOverrides;
  resolutionOverride: string;
  isDirty: boolean;
  initFromPreview: (
    sections: ScriptSection[],
    resolution?: string,
    ttsProvider?: string,
    voiceId?: string
  ) => void;
  updateSection: (index: number, changes: Partial<EditableSection>) => void;
  deleteSection: (index: number) => void;
  addSection: () => void;
  moveSection: (index: number, direction: 'up' | 'down') => void;
  setVoiceOverrides: React.Dispatch<React.SetStateAction<VoiceOverrides>>;
  setResolutionOverride: (resolution: string) => void;
  buildFullNarration: () => string;
  buildOverrides: () => ScriptEditOverrides;
  reset: () => void;
}

export function useScriptEditState(): UseScriptEditStateReturn {
  const [sections, setSections] = useState<EditableSection[]>([]);
  const [voiceOverrides, setVoiceOverrides] = useState<VoiceOverrides>({});
  const [resolutionOverride, setResolutionOverride] = useState('');
  const [originalHash, setOriginalHash] = useState('');

  const isDirty = useMemo(() => {
    const currentHash = JSON.stringify({ sections, voiceOverrides, resolutionOverride });
    return currentHash !== originalHash;
  }, [sections, voiceOverrides, resolutionOverride, originalHash]);

  const initFromPreview = useCallback(
    (
      previewSections: ScriptSection[],
      resolution?: string,
      ttsProvider?: string,
      voiceId?: string
    ) => {
      const editable: EditableSection[] = previewSections.map((s) => ({
        title: s.title,
        narration_text: s.narration_text,
        duration_seconds: s.duration_seconds,
        isExpanded: true,
      }));
      const voice: VoiceOverrides = {};
      if (ttsProvider) voice.tts_provider = ttsProvider;
      if (voiceId) voice.voice_id = voiceId;
      const res = resolution || '';

      setSections(editable);
      setVoiceOverrides(voice);
      setResolutionOverride(res);
      setOriginalHash(
        JSON.stringify({ sections: editable, voiceOverrides: voice, resolutionOverride: res })
      );
    },
    []
  );

  const updateSection = useCallback((index: number, changes: Partial<EditableSection>) => {
    setSections((prev) => prev.map((s, i) => (i === index ? { ...s, ...changes } : s)));
  }, []);

  const deleteSection = useCallback((index: number) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addSection = useCallback(() => {
    setSections((prev) => [
      ...prev,
      {
        title: `Section ${prev.length + 1}`,
        narration_text: '',
        duration_seconds: 15,
        isExpanded: true,
      },
    ]);
  }, []);

  const moveSection = useCallback((index: number, direction: 'up' | 'down') => {
    setSections((prev) => {
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }, []);

  const buildFullNarration = useCallback(() => {
    return sections.map((s) => s.narration_text).join('\n\n');
  }, [sections]);

  const buildOverrides = useCallback((): ScriptEditOverrides => {
    const overrides: ScriptEditOverrides = {};
    if (voiceOverrides.tts_provider) overrides.tts_provider = voiceOverrides.tts_provider;
    if (voiceOverrides.voice_id) overrides.voice_id = voiceOverrides.voice_id;
    if (resolutionOverride) overrides.resolution = resolutionOverride;
    overrides.sections = sections.map((s) => ({
      title: s.title,
      narration_text: s.narration_text,
      duration_seconds: s.duration_seconds,
    }));
    return overrides;
  }, [sections, voiceOverrides, resolutionOverride]);

  const reset = useCallback(() => {
    setSections([]);
    setVoiceOverrides({});
    setResolutionOverride('');
    setOriginalHash('');
  }, []);

  return {
    sections,
    voiceOverrides,
    resolutionOverride,
    isDirty,
    initFromPreview,
    updateSection,
    deleteSection,
    addSection,
    moveSection,
    setVoiceOverrides,
    setResolutionOverride,
    buildFullNarration,
    buildOverrides,
    reset,
  };
}
